// =============================================================================
// Internal Admin Review  —  route: /admin/leads
// Lists funnel submissions from the server database. Filter by tier and by lead
// quality flag, sort by newest or highest score, and open a full-answer detail
// view. Qualified applicants are highlighted.
//
// AUTH: the user types a passcode; it is sent to the API as a Bearer token and
// validated server-side against ADMIN_TOKEN (see server/.env). The passcode is
// never baked into the JS bundle. This is lightweight gating, not full auth —
// TODO: move to proper authentication before exposing this widely.
// =============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchLeads } from './leadStore';
import {
  TIER,
  TIER_LABELS,
  QUESTION_LABELS,
  ANSWER_ORDER,
  LEAD_FLAGS,
  formatAnswer,
  formatFlag,
} from './funnelConfig';

const TOKEN_KEY = 'rt_admin_token';

const TIER_BADGE = {
  [TIER.QUALIFIED]: 'bg-green-100 text-green-700',
  [TIER.NURTURE]: 'bg-amber-100 text-amber-700',
  [TIER.NOT_FIT]: 'bg-gray-200 text-gray-600',
};

const FLAG_BADGE = {
  positive: 'bg-green-100 text-green-700',
  negative: 'bg-amber-100 text-amber-800',
};

const TIER_FILTERS = [
  { value: 'all', label: 'All' },
  { value: TIER.QUALIFIED, label: 'Qualified' },
  { value: TIER.NURTURE, label: 'Nurture' },
  { value: TIER.NOT_FIT, label: 'Not Fit' },
];

const FLAG_KEYS = Object.keys(LEAD_FLAGS);

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function flagTone(key) {
  return (LEAD_FLAGS[key] && LEAD_FLAGS[key].tone) || 'negative';
}

/** Small coloured pill for a single lead-quality flag. */
function FlagBadge({ flagKey }) {
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${
        FLAG_BADGE[flagTone(flagKey)]
      }`}
    >
      {formatFlag(flagKey)}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Passcode gate
// -----------------------------------------------------------------------------
function PasscodeGate({ onSubmit, error, loading }) {
  const [value, setValue] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (value.trim()) onSubmit(value.trim());
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-revolution-navy px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl">
        <h1 className="text-xl font-extrabold text-revolution-navy">Internal Lead Review</h1>
        <p className="mt-1 text-sm text-gray-500">Enter the team passcode to continue.</p>
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Passcode"
          autoFocus
          className="mt-5 w-full rounded-lg border border-gray-300 px-4 py-3 outline-none focus:border-revolution-blue focus:ring-2 focus:ring-blue-100"
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full rounded-lg bg-revolution-orange px-4 py-3 font-bold text-white transition hover:bg-orange-600 disabled:opacity-60"
        >
          {loading ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Lead detail panel
// -----------------------------------------------------------------------------
function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right font-medium text-revolution-navy">{value}</dd>
    </div>
  );
}

function LeadDetail({ lead, onClose }) {
  const utm = lead.utm || {};
  const flags = lead.flags || [];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-extrabold text-revolution-navy">
              {lead.firstName} {lead.lastName}
            </h2>
            <p className="text-sm text-gray-500">{formatDate(lead.createdAt)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span
            className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${
              TIER_BADGE[lead.tier] || 'bg-gray-200 text-gray-600'
            }`}
          >
            {TIER_LABELS[lead.tier] || lead.tier}
          </span>
          <span className="text-sm font-semibold text-revolution-navy">Score: {lead.score}</span>
          {lead.tier === TIER.QUALIFIED && (
            <span className="text-sm text-gray-500">
              {lead.emailSent ? 'Email sent ✓' : 'Email not sent'}
            </span>
          )}
        </div>

        {flags.length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-400">
              Lead quality flags
            </h3>
            <div className="flex flex-wrap gap-2">
              {flags.map((f) => (
                <FlagBadge key={f} flagKey={f} />
              ))}
            </div>
          </div>
        )}

        <dl className="mt-6 space-y-3 text-sm">
          <Row
            label="Email"
            value={
              <a className="text-revolution-blue" href={`mailto:${lead.email}`}>
                {lead.email}
              </a>
            }
          />
          <Row
            label="Phone"
            value={
              <a className="text-revolution-blue" href={`tel:${lead.phone}`}>
                {lead.phone}
              </a>
            }
          />
          <Row label="Location" value={lead.location} />
        </dl>

        <h3 className="mt-7 text-sm font-bold uppercase tracking-wide text-gray-400">
          Application answers
        </h3>
        <dl className="mt-3 space-y-4">
          {ANSWER_ORDER.map((key) => (
            <div key={key}>
              <dt className="text-sm font-semibold text-revolution-navy">{QUESTION_LABELS[key]}</dt>
              <dd className="mt-0.5 whitespace-pre-wrap text-sm text-gray-600">
                {key === 'whyInterested' || key === 'successReason'
                  ? lead[key] || '—'
                  : formatAnswer(lead[key])}
              </dd>
            </div>
          ))}
        </dl>

        {lead.scoreBreakdown && lead.scoreBreakdown.length > 0 && (
          <>
            <h3 className="mt-7 text-sm font-bold uppercase tracking-wide text-gray-400">
              Score breakdown
            </h3>
            <ul className="mt-3 space-y-1.5 text-sm">
              {lead.scoreBreakdown.map((item, i) => (
                <li key={i} className="flex justify-between gap-4">
                  <span className="text-gray-600">{item.label}</span>
                  <span
                    className={`font-semibold ${
                      item.points > 0
                        ? 'text-green-600'
                        : item.points < 0
                        ? 'text-red-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {item.points > 0 ? `+${item.points}` : item.points}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        <h3 className="mt-7 text-sm font-bold uppercase tracking-wide text-gray-400">Attribution</h3>
        <dl className="mt-3 space-y-2 text-sm">
          <Row label="Source" value={lead.source} />
          <Row label="utm_source" value={utm.utm_source || '—'} />
          <Row label="utm_medium" value={utm.utm_medium || '—'} />
          <Row label="utm_campaign" value={utm.utm_campaign || '—'} />
          <Row label="utm_content" value={utm.utm_content || '—'} />
          <Row label="utm_term" value={utm.utm_term || '—'} />
          <Row label="fbclid" value={utm.fbclid || '—'} />
        </dl>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main admin page
// -----------------------------------------------------------------------------
function AdminLeads() {
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [flagFilter, setFlagFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (token, { silent = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchLeads(token);
      setLeads(data);
      setAuthed(true);
      try {
        window.sessionStorage.setItem(TOKEN_KEY, token);
      } catch (e) {
        /* ignore */
      }
    } catch (err) {
      if (err.code === 'unauthorized') {
        try {
          window.sessionStorage.removeItem(TOKEN_KEY);
        } catch (e) {
          /* ignore */
        }
      }
      if (!silent) setError(err.message || 'Could not load leads.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-login if a passcode from earlier this session is still stored.
  useEffect(() => {
    let saved = '';
    try {
      saved = window.sessionStorage.getItem(TOKEN_KEY) || '';
    } catch (e) {
      /* ignore */
    }
    if (saved) load(saved, { silent: true });
  }, [load]);

  const tierCounts = useMemo(() => {
    const c = { all: leads.length };
    leads.forEach((l) => {
      c[l.tier] = (c[l.tier] || 0) + 1;
    });
    return c;
  }, [leads]);

  const visible = useMemo(() => {
    let list = leads;
    if (tierFilter !== 'all') list = list.filter((l) => l.tier === tierFilter);
    if (flagFilter !== 'all') {
      list = list.filter((l) => Array.isArray(l.flags) && l.flags.includes(flagFilter));
    }
    list = [...list];
    if (sortBy === 'score') {
      list.sort((a, b) => (b.score || 0) - (a.score || 0));
    } else {
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return list;
  }, [leads, tierFilter, flagFilter, sortBy]);

  const refresh = () => {
    try {
      const token = window.sessionStorage.getItem(TOKEN_KEY);
      if (token) load(token);
    } catch (e) {
      /* ignore */
    }
  };

  if (!authed) {
    return <PasscodeGate onSubmit={(token) => load(token)} error={error} loading={loading} />;
  }

  const selectClass =
    'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:border-revolution-blue';

  return (
    <div className="min-h-screen bg-revolution-gray">
      <header className="bg-revolution-navy px-4 py-5 text-white sm:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-extrabold sm:text-xl">Funnel Lead Review</h1>
            <p className="text-sm text-blue-200">Entrepreneur Application — careers-funnel</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-blue-200 sm:inline">{leads.length} total</span>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold transition hover:bg-white/20 disabled:opacity-50"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Tier filters */}
        <div className="mb-4 flex flex-wrap gap-2">
          {TIER_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setTierFilter(f.value)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                tierFilter === f.value
                  ? 'bg-revolution-navy text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {f.label} ({tierCounts[f.value] || 0})
            </button>
          ))}
        </div>

        {/* Flag filter + sort */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Flag
            <select
              value={flagFilter}
              onChange={(e) => setFlagFilter(e.target.value)}
              className={selectClass}
            >
              <option value="all">All flags</option>
              {FLAG_KEYS.map((key) => (
                <option key={key} value={key}>
                  {formatFlag(key)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Sort
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={selectClass}
            >
              <option value="newest">Newest first</option>
              <option value="score">Highest score</option>
            </select>
          </label>
          <span className="text-sm text-gray-500">{visible.length} shown</span>
        </div>

        {visible.length === 0 ? (
          <div className="rounded-xl bg-white p-10 text-center text-gray-500 shadow-sm">
            {loading ? 'Loading leads…' : 'No submissions match these filters.'}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl bg-white shadow-sm">
            {/* Desktop table */}
            <table className="hidden w-full text-left text-sm sm:table">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Phone</th>
                  <th className="px-5 py-3">Score</th>
                  <th className="px-5 py-3">Tier</th>
                  <th className="px-5 py-3">Flags</th>
                  <th className="px-5 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((lead) => {
                  const isQualified = lead.tier === TIER.QUALIFIED;
                  return (
                    <tr
                      key={lead.id}
                      onClick={() => setSelected(lead)}
                      className={`cursor-pointer ${
                        isQualified
                          ? 'bg-green-50 hover:bg-green-100'
                          : 'hover:bg-blue-50'
                      }`}
                    >
                      <td
                        className={`px-5 py-3 font-semibold text-revolution-navy ${
                          isQualified ? 'border-l-4 border-green-500' : 'border-l-4 border-transparent'
                        }`}
                      >
                        {lead.firstName} {lead.lastName}
                      </td>
                      <td className="px-5 py-3 text-gray-600">{lead.email}</td>
                      <td className="px-5 py-3 text-gray-600">{lead.phone}</td>
                      <td className="px-5 py-3 font-semibold text-revolution-navy">{lead.score}</td>
                      <td className="px-5 py-3">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ${
                            TIER_BADGE[lead.tier] || 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {TIER_LABELS[lead.tier] || lead.tier}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(lead.flags || []).slice(0, 3).map((f) => (
                            <FlagBadge key={f} flagKey={f} />
                          ))}
                          {(lead.flags || []).length > 3 && (
                            <span className="text-xs text-gray-400">
                              +{lead.flags.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-500">{formatDate(lead.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile cards */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {visible.map((lead) => {
                const isQualified = lead.tier === TIER.QUALIFIED;
                return (
                  <li
                    key={lead.id}
                    onClick={() => setSelected(lead)}
                    className={`cursor-pointer p-4 ${
                      isQualified ? 'border-l-4 border-green-500 bg-green-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-revolution-navy">
                        {lead.firstName} {lead.lastName}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ${
                          TIER_BADGE[lead.tier] || 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {TIER_LABELS[lead.tier] || lead.tier}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{lead.email}</p>
                    <p className="text-sm text-gray-500">
                      Score {lead.score} · {formatDate(lead.createdAt)}
                    </p>
                    {(lead.flags || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {lead.flags.map((f) => (
                          <FlagBadge key={f} flagKey={f} />
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </main>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default AdminLeads;
